/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import AudioManager from '../../../Audio/AudioManager';
import { RecallEntryData } from '../../../Game/RecallEntry';
import LocalizationComponent from '../../../Localization/LocalizationComponent';
import IPlaybar from '../../../Playbar/IPlaybar';
import StateDependencyComponent from '../../../StateManager/StateDependencyComponent';
import StateEvent from '../../../StateManager/StateEvent';
import { StateID } from '../../../StateManager/StateID';
import StateReference from '../../../StateManager/StateReference';
import CLog, { LogTypes } from '../../ComponentLogger';
import TypeStrings from '../../TypeStrings';
import InjectableComponent from '../ComponentReference/InjectableComponent';
import ReferencedComponent from '../ComponentReference/ReferencedComponent';
import { ReferenceGroup } from '../ComponentReference/ReferenceGroup';
import { ContextKey } from '../Configurator/ContextKey';
import ObservableStringComponent from '../Observable/ObservableStringComponent';
import IPopupManager from './IPopupManager';
import PageViewProperties from './PageViewProperties';
import PopupDispatcher, { PopupCloseOption } from './PopupDispatcher';
import RecallPage from './RecallPage';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Component for displaying multiple PageView elements and menu system.
 */
@cc._decorator.ccclass
@cc._decorator.executeInEditMode
export default class PopupManager extends StateDependencyComponent implements IPopupManager
{
    /** Title of the current PageView. */
    @property({ type: ObservableStringComponent })
    protected currentTitleObservable: ObservableStringComponent = null;

    /** Display node for indicating current PageView. */
    @property({ type: cc.Sprite, visible: true })
    private _indicatorSprite: cc.Sprite = null;
    private _indicatorSpriteFrame: cc.SpriteFrame = null;

    /** Parent node containing all PageViews. */
    @property({ type: cc.Node, visible: true })
    private _pagesNode: cc.Node = null;

    /** Node containing the content of the help view. */
    @property({ type: cc.Layout, visible: true })
    private _helpContent: cc.Layout = null;

    /** Layout containing AutoPlay options. */
    @property({ type: cc.Layout, visible: true })
    protected autoPlayLayout: cc.Layout = null;

    @property({ type: cc.PageView.Direction })
    protected indicatorDirection: cc.PageView.Direction = cc.PageView.Direction.Vertical;

    @property({ type: [cc.Sprite] })
    protected overrideIndicators: cc.Sprite[] = [];

    @property({ type: InjectableComponent })
    protected audioManagerRef: InjectableComponent<AudioManager> = new InjectableComponent<AudioManager>();

    @property({ type: InjectableComponent })
    protected playbarRef: InjectableComponent<IPlaybar> = new InjectableComponent<IPlaybar>();

    @property({ type: InjectableComponent })
    protected paytableRef: InjectableComponent<ReferencedComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, "Paytable");

    /** @hidden */
    private _localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();

    /** Currently displayed PageView. */
    private _currentPageView?: cc.PageView = null;

    /** Pages under Pages Node. */
    private _childPages: cc.PageView[] = [];

    private _initState: StateReference = new StateReference(StateID.CheckRecall, false, ReferenceGroup.MainGame);

    /** Menu buttons that can select PageView. */
    private _menuItems: PopupDispatcher[] = [];

    private _recallPage: RecallPage = null;

    /** Whether the popup manager open request has been called. */
    private _hasUserOpened: boolean = false;
    private _hasInjectedPaytable: boolean = false;
    public get HasUserOpened(): boolean { return this._hasUserOpened; }

    public get IsOpen(): boolean { return this.node.active; }

    /** @hidden */
    protected setContexts(): void
    {
        if (CC_EDITOR) { return; }

        this.audioManagerRef.SetContext(AudioManager);
        this.paytableRef.SetContext(ReferencedComponent);
        this.playbarRef.SetContext(ContextKey.PLAYBAR);
        this._localRef.SetContext(LocalizationComponent);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.updateEditorElements();

        if (CC_EDITOR) { return; }

        this._indicatorSprite = this._indicatorSprite.getComponent(cc.Sprite);
        this._indicatorSpriteFrame = this._indicatorSprite.spriteFrame;

        this._recallPage = this.getComponentInChildren(RecallPage);
    }

    /** @hidden */
    protected start(): void
    {
        if (CC_EDITOR) { return; }

        super.start(this._initState);

        if (this._childPages.length < 1)
        {
            CLog.warn('No pagviews loaded.', LogTypes.Initialization, this);
        }

        this.configureAudio();
        // Purposely usings public function to prevent re-injection.
        this.InjectPaytable();
    }

    public InjectPaytable(): void
    {
        if (!this.paytableRef.Value || this._hasInjectedPaytable) { return; }

        this.injectPaytable();
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent) =>
    {
        if (event.State === this._initState.State)
        {
            this.deactivateChildren();
            if (!this._hasUserOpened) { this.Disable(); }
        }
    }

    /** @hidden */
    protected onDisable(): void
    {
        this.Disable();
    }

    /** @hidden */
    protected onFocusInEditor(): void
    {
        this.updateEditorElements();
    }

    /** Updates ui related lists.  */
    private updateEditorElements(): void
    {
        this._childPages = this.node.getComponentsInChildren(cc.PageView);

        this._menuItems = this.node.getComponentsInChildren(PopupDispatcher);
        this.overrideIndicators.length = this._menuItems.length;
    }

    /** Configure audio PageView. */
    private configureAudio(): void
    {
        if (CC_EDITOR) { return; }

        if (!this.audioManagerRef.Value) { return; }
    }

    /** @hidden */
    private injectPaytable(): void
    {
        if (!this.paytableRef.HasValue()) { return; }

        this.paytableRef.Value.node.removeFromParent();
        this._helpContent.node.insertChild(this.paytableRef.Value.node, 0);
        this.paytableRef.Value.node.setPosition(cc.Vec2.ZERO);

        this._helpContent.updateLayout();

        this._hasInjectedPaytable = true;
    }

    public ClearRecallEntries(): void
    {
        if (!this._recallPage) { return; }
        this._recallPage.ClearRecallData();
    }

    public UpdateRecallEntries(recallEntryData: RecallEntryData[]): void
    {
        if (!this._recallPage) { return; }
        this._recallPage.AddRecallEntries(recallEntryData);
    }

    /** Hides the [[PopupManager]] from display. */
    public Disable(): void
    {
        if (!this.enabled || CC_EDITOR) { return; }

        if (this._currentPageView)
        {
            this._currentPageView.node.active = false;
            this._currentPageView = null;
        }

        this.enabled = false;
        this.node.active = false;

        this.configurePlaybar(true);
    }

    /**
     * Shows the [[PopupManger]] with selected PageView.
     * @param pageView PageView to open.
     * @param closeOption How to handle closing already open [[PoupManager]].
     */
    public ShowPopup(pageView: cc.PageView | string, closeOption: PopupCloseOption): PopupManager
    {
        this._hasUserOpened = true;

        let newPageView: cc.PageView = this.getPageView(pageView);

        if (this.shouldClose(newPageView, closeOption))
        {
            this.Disable();
            return this;
        }

        if (newPageView === this._currentPageView) { return this; }

        this.deactivateChildren();
        this.configurePlaybar(false);

        this.setPageTitle(newPageView);

        this.node.active = true;
        this.enabled = true;

        if (newPageView)
        {
            newPageView.node.active = true;
            newPageView.setCurrentPageIndex(0);
        }

        this._currentPageView = newPageView;
        this.scheduleOnce(() => this.updateIndicatorDisplay());
        return this;
    }

    private configurePlaybar(enable: boolean): void
    {
        this.playbarRef.WithValue(playbar =>
            {
                if (enable)
                {
                    playbar.ToggleOn();
                    playbar.ShowPlayButton();
                }
                else
                {
                    playbar.ToggleOff(true);
                    playbar.HidePlayButton();
                }
            });
    }

    private setPageTitle(pageView: cc.PageView): void
    {
        if (!pageView || !this.currentTitleObservable) { return; }

        const pageProperties: PageViewProperties = pageView.getComponent(PageViewProperties);
        if (!pageProperties) { return; }

        let titleValue: string = pageProperties.Title;

        this._localRef.WithValue(local =>
            {
                titleValue = local.GetStringResource(titleValue) || titleValue;
            });

        this.currentTitleObservable.Value = titleValue ? titleValue.toUpperCase() : '';
    }

    private getPageView(pageView: cc.PageView | string): cc.PageView
    {
        if (typeof(pageView) !== TypeStrings.string) { return pageView as cc.PageView; }

        if (pageView === '')
        {
            const dispatcher: PopupDispatcher = this._menuItems.find(item => item.node.parent.active);
            if (!dispatcher || !dispatcher.PageViewNodeName) { return null; }

            pageView = dispatcher.PageViewNodeName;
        }

        return this.getPageByName(pageView as string) || null;
    }

    /** @hidden */
    private shouldClose(pageView: cc.PageView, closeOption: PopupCloseOption): boolean
    {
        switch (closeOption)
        {
            case PopupCloseOption.CloseIfOpen:
                return this.enabled;
            case PopupCloseOption.CloseIfPageOpen:
                return (this.enabled && this._currentPageView === pageView);
            case PopupCloseOption.None:
            default:
                return false;
        }
    }

    /** Add new PageView. */
    public AddPage(pageViewNode: cc.Node): void
    {
        const pageView: cc.PageView = pageViewNode.getComponent(cc.PageView);
        if (!pageView)
        {
            CLog.error('Trying to add non PageView.', this, LogTypes.PopupManager);
            return;
        }

        pageViewNode.parent = this._pagesNode;
        this._childPages.push(pageView);
    }

    /** Handles changes to display and updates the indicator. */
    protected updateIndicatorDisplay(): void
    {
        if (CC_EDITOR) { return; }

        if (!this._indicatorSprite) { return; }

        if (this._currentPageView === null)
        {
            this._indicatorSprite.enabled = false;
            return;
        }
        else
        {
            this._currentPageView.node.active = true;
            this._currentPageView.setCurrentPageIndex(0);
        }

        const activeMenuItem: PopupDispatcher
            = this._menuItems.find((menuItem) => menuItem.PageViewNodeName === this._currentPageView.node.name);

        if (!activeMenuItem)
        {
            this._indicatorSprite.enabled = false;
            return;
        }

        this._indicatorSprite.enabled = true;

        this.indicatorPositionHandler(activeMenuItem.node);
        this.overrideIndicatorHandler();
    }

    /** Get PageView by name. */
    private getPageByName(pageName: string): cc.PageView
    {
        return this._childPages.find(childPage => childPage.node.name === pageName);
    }

    /** Set all child node active to false. */
    private deactivateChildren(): void
    {
        if (CC_EDITOR) { return; }

        this._childPages.forEach(childPage => childPage.node.active = false);
    }

    /** @hidden */
    private indicatorPositionHandler(menuNode: cc.Node): void
    {
        const buttonWorldSpace: cc.Vec2 = menuNode.convertToWorldSpaceAR(menuNode.position);
        const buttonNodeSpace: cc.Vec2 = this._indicatorSprite.node.parent.convertToNodeSpaceAR(buttonWorldSpace);

        switch (this.indicatorDirection)
        {
            case cc.PageView.Direction.Vertical:
                this._indicatorSprite.node.y = buttonNodeSpace.y;
                break;
            case cc.PageView.Direction.Horizontal:
                this._indicatorSprite.node.x = buttonNodeSpace.x;
                break;
            default:
                break;
        }
    }

    /** @hidden */
    private overrideIndicatorHandler(): void
    {
        const overrideSprite: cc.Sprite = this.overrideIndicators[this._childPages.indexOf(this._currentPageView)];

        if (overrideSprite)
        {
            this._indicatorSprite.spriteFrame = overrideSprite.spriteFrame;
        }
        else if (this._indicatorSprite.spriteFrame !== this._indicatorSpriteFrame)
        {
            this._indicatorSprite.spriteFrame = this._indicatorSpriteFrame;
        }
    }
}
